import type { paths } from '../shared/api/schema';

/**
 * Every backend endpoint the website calls. `satisfies` checks each one against the types generated from
 * the backend (pnpm gen:api): an endpoint that is renamed or removed there fails the type check here.
 * The WebSocket namespace is part of the socket contract: shared/socket/socket-events.ts.
 */
export const apiRoutes = {
  auth: {
    login: '/api/auth/login',
    refresh: '/api/auth/refresh',
    logout: '/api/auth/logout',
    me: '/api/auth/me',
  },
  jobs: {
    list: '/api/jobs',
    counts: '/api/jobs/counts',
    startTest: '/api/jobs/test',
  },
  fetch: {
    runs: '/api/fetch/runs',
    run: '/api/fetch/runs/{id}',
    runPages: '/api/fetch/runs/{id}/pages',
    page: '/api/fetch/pages/{id}',
    retryPage: '/api/fetch/pages/{id}/retry',
    proxies: '/api/fetch/proxies',
  },
  imports: {
    list: '/api/imports',
    detail: '/api/imports/{id}',
    items: '/api/imports/{id}/items',
  },
  products: {
    list: '/api/products',
    counts: '/api/products/counts',
    detail: '/api/products/{id}',
  },
  weight: {
    tests: '/api/weight/tests',
    test: '/api/weight/tests/{id}',
    retryNow: '/api/weight/tests/{id}/retry-now',
    research: '/api/weight/tests/{id}/research',
    categories: '/api/weight/categories',
    rules: '/api/weight/rules',
    settings: '/api/weight/settings',
    rule: '/api/weight/rules/{key}',
    category: '/api/weight/categories/{key}',
  },
  shopify: {
    status: '/api/shopify/status',
    send: '/api/shopify/send',
    dropbot: '/api/shopify/dropbot',
    availability: '/api/shopify/availability',
    remove: '/api/shopify/remove',
    trim: '/api/shopify/dropbot/trim',
    census: '/api/shopify/dropbot/census',
    unsellableSheet: '/api/shopify/dropbot/census/unsellable.xlsx',
    cleanup: '/api/shopify/dropbot/cleanup',
    missing: '/api/shopify/dropbot/missing',
    stopped: '/api/shopify/dropbot/stopped',
    soldOutPrices: '/api/shopify/dropbot/sold-out-prices',
    sendMissing: '/api/shopify/dropbot/missing/send',
    cleanupSheet: '/api/shopify/dropbot/cleanup.xlsx',
  },
  categories: {
    list: '/api/categories',
    refresh: '/api/categories/refresh',
    rules: '/api/categories/rules',
    rule: '/api/categories/rules/{id}',
    product: '/api/categories/products/{productId}',
    accept: '/api/categories/products/{productId}/accept',
  },
  review: {
    list: '/api/review',
    accept: '/api/review/{productId}/accept',
    setWeight: '/api/review/{productId}/weight',
    keep: '/api/review/{productId}/keep',
  },
  weightModel: {
    cleaner: '/api/weight-model/cleaner',
    ubuy: '/api/weight-model/ubuy',
    datasets: '/api/weight-model/datasets',
    models: '/api/weight-model/models',
    model: '/api/weight-model/models/{name}',
    makeLive: '/api/weight-model/models/{name}/live',
    estimateForTest: '/api/weight-model/estimates/tests/{id}',
    suspects: '/api/weight-model/suspects',
    reviewSuspect: '/api/weight-model/suspects/{id}/review',
  },
  research: {
    data: '/api/weight-research/data',
    dataCategories: '/api/weight-research/data/categories',
  },
  imageComparison: {
    latest: '/api/image-comparison',
  },
  weightBenchmark: {
    state: '/api/weight-benchmark',
    run: '/api/weight-benchmark/run',
  },
  storeChecks: {
    list: '/api/store-checks',
    detail: '/api/store-checks/{id}',
    items: '/api/store-checks/{id}/items',
    item: '/api/store-checks/items/{itemId}',
    measured: '/api/store-checks/items/{itemId}/measured',
    pause: '/api/store-checks/{id}/pause',
    resume: '/api/store-checks/{id}/resume',
    stop: '/api/store-checks/{id}/stop',
    retryFailed: '/api/store-checks/{id}/retry-failed',
    decideAgain: '/api/store-checks/{id}/decide-again',
    recheckRough: '/api/store-checks/{id}/recheck-rough',
    variants: '/api/store-checks/{id}/variants',
    export: '/api/store-checks/{id}/export',
  },
  ai: {
    usage: '/api/ai/usage',
    calls: '/api/ai/calls',
    settings: '/api/ai/settings',
    testAlert: '/api/ai/alerts/test',
  },
  catalog: {
    known: '/api/catalog/known',
    refreshKnown: '/api/catalog/known/refresh',
  },
} as const satisfies Record<string, Record<string, keyof paths>>;
